import { DateTime, Duration } from "luxon";
import { getCurrentLocale } from "../use/locale-switcher";
export type RangeMode = "last_8h" | "last_24h" | "last_48h" | "last_7d" | "custom";
export type PredefinedRangeMode = Exclude<RangeMode, "custom">;

export function generateTimeRangeOffset(mode: PredefinedRangeMode): Duration {
	const hoursMapping: Record<PredefinedRangeMode, number> = {
		last_8h: 8,
		last_24h: 24,
		last_48h: 48,
		last_7d: 24 * 7,
	};

	return Duration.fromObject({ hour: hoursMapping[mode] });
}

type AnyDT = string | Date | DateTime;
function parseAnyDT(dt: AnyDT, zone?: string, locale?: string): DateTime {
	if (typeof dt === "string") {
		return DateTime.fromISO(dt, {
			zone,
			locale,
		});
	}

	if (DateTime.isDateTime(dt)) {
		return dt;
	}

	return DateTime.fromJSDate(dt, { zone }).reconfigure({ locale });
}

type FormatTimeRangeOpts = {
	timezone?: string;
	showSeconds?: boolean;
};

export function formatTimeRange(
	start: AnyDT,
	end: AnyDT,
	{ timezone = "local", showSeconds = false }: FormatTimeRangeOpts = {}
) {
	const locale = getCurrentLocale();

	const startDT = parseAnyDT(start, timezone, locale);
	const endDT = parseAnyDT(end, timezone, locale);

	const isSameDay = startDT.hasSame(endDT, "day");

	const dateFormat = DateTime.DATE_MED;
	const timeFormat = showSeconds ? DateTime.TIME_WITH_SECONDS : DateTime.TIME_SIMPLE;

	const timeSep = "-";
	const rangeSep = "—";
	const dtSep = "·";

	const formatDate = (dt: DateTime) => dt.toLocaleString(dateFormat);
	const formatTime = (dt: DateTime) => dt.toLocaleString(timeFormat);
	const formatDateTime = (dt: DateTime) => `${formatDate(dt)} ${dtSep} ${formatTime(dt)}`;

	if (isSameDay) {
		return `${formatDateTime(startDT)} ${timeSep} ${formatTime(endDT)}`;
	}

	return `${formatDateTime(startDT)} ${rangeSep} ${formatDateTime(endDT)}`;
}
